import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useApiRequest, useAuthenticatedQuery } from "@/hooks/use-api";
import { Order } from "@shared/schema";
import { OrderFormData } from "@/types/order";

// API-specific type for orders list endpoint
export type OrderListResponse = Order;

interface UseOrdersResult {
  orders: OrderListResponse[];
  isLoading: boolean;
  error: unknown;
}

export const useOrders = (): UseOrdersResult => {
  const { user } = useUser();
  const { organization } = useOrganization();

  const { data, isLoading, error } = useAuthenticatedQuery(
    ["/api/orders", organization?.id],
    {
      enabled: !!user && !!organization,
    },
  );

  return {
    orders: (data as OrderListResponse[]) || [],
    isLoading,
    error,
  };
};

// Hook for creating orders
interface CreateOrderParams {
  orderData: OrderFormData;
}

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async ({ orderData }: CreateOrderParams) => {
      const response = await apiRequest("POST", "/api/orders", orderData);

      if (!response.ok) {
        throw new Error(`Failed to create order: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch orders list
      queryClient.invalidateQueries({
        queryKey: ["/api/orders", organization?.id],
      });
    },
  });
};

// Hook for updating orders
interface UpdateOrderParams {
  orderId: string;
  updateData: Partial<{
    orderStatus: string;
    fulfillmentType: "delivery" | "pickup";
    title: string;
    description: string;
    notes: string;
    expectedDeliveryDate: string;
    products: any[]; // Product updates for autosave
  }>;
}

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async ({ orderId, updateData }: UpdateOrderParams) => {
      const response = await apiRequest(
        "PATCH",
        `/api/orders/${orderId}`,
        updateData,
      );

      if (!response.ok) {
        throw new Error(`Failed to update order: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch orders list
      queryClient.invalidateQueries({
        queryKey: ["/api/orders", organization?.id],
      });
      // Invalidate the specific order that was updated to prevent staleness
      queryClient.invalidateQueries({
        queryKey: [`/api/orders/${variables.orderId}`, organization?.id],
      });
    },
  });
};

// Hook for fetching single order
export const useOrder = (orderId: string) => {
  const { user } = useUser();
  const { organization } = useOrganization();

  return useAuthenticatedQuery<Order>(
    [`/api/orders/${orderId}`, organization?.id],
    {
      enabled: !!user && !!organization && !!orderId,
    },
  );
};

// Hook for submitting orders to Hajoca
interface SubmitOrderParams {
  orderId: string;
  hajocaCustomerId: string;
}

export const useSubmitOrder = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async ({ orderId, hajocaCustomerId }: SubmitOrderParams) => {
      const response = await apiRequest(
        "POST",
        `/api/orders/${orderId}/submit`,
        {
          hajocaCustomerId,
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to submit order: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch orders list
      queryClient.invalidateQueries({
        queryKey: ["/api/orders", organization?.id],
      });
    },
  });
};

// Hook for deleting orders
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const response = await apiRequest("DELETE", `/api/orders/${orderId}`);

      if (!response.ok) {
        throw new Error(`Failed to delete order: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/orders", organization?.id],
      });
    },
  });
};

// Hook for querying order fulfillment status from Hajoca
interface OrderInquiryParams {
  orderId: string;
  shipToId?: string;
}

export const useOrderInquiry = () => {
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async ({ orderId, shipToId }: OrderInquiryParams) => {
      const queryString = shipToId ? `?shipToId=${shipToId}` : "";
      const response = await apiRequest(
        "GET",
        `/api/orders/${orderId}/inquiry${queryString}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to query order status: ${response.status}`);
      }

      return await response.json();
    },
  });
};
